import {
  Button,
  EmptyState,
  PageHeader,
  PageTitle,
  RowActions,
  SearchInput,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
  formatDate,
  formatRelative,
  formatTime,
} from '@westhetics/ui'
import { useState } from 'react'
import { Link } from 'react-router'
import { ListFooter } from '@/app/list-footer'
import { LoadError } from '@/app/load-error'
import { PeriodFilter } from '@/app/period-filter'
import { Page } from '@/app/shell'
import { todayIso } from '@/lib/dates'
import { useSession } from '@/lib/session'
import { useDebounced } from '@/lib/use-debounced'
import { useStoredFilter } from '@/lib/use-stored-filter'
import { useAuditLog, type AuditEntry, type AuditSort } from './api'
import {
  ACTION_OPTIONS,
  TARGET_FILTERS,
  TARGET_TYPE_GROUPS,
  actionLabel,
  actorName,
  actorNote,
  canFilterByActor,
  openTargetLabel,
  targetName,
  targetRoute,
  targetTypeLabel,
  type TargetFilter,
  type TargetViewer,
} from './labels'

/**
 * Trilha (docs/areas/administracao.md, "Trilha"; RN-ADM-60, RT-22). Arquétipo: lista de trabalho
 * só de leitura, de gestora e de root com clínica ativa. A pergunta: "quem fez o quê nesta clínica,
 * e quando?". Não há pendência nem ação primária: a trilha registra, não cobra. Abre nos últimos
 * 30 dias, do mais recente para o mais antigo; a linha abre em si mesma o detalhe (P-ADM-90 a 93).
 */

const DEFAULT_DAYS = 30
const WIDER_DAYS = 90

/** `aaaa-mm-dd` de `days` dias atrás, no relógio de quem usa. */
function daysAgoIso(days: number, now: Date = new Date()) {
  return todayIso(new Date(now.getTime() - days * 86_400_000))
}

/** Quem agiu ou o que foi atingido, fixado pela própria linha ("só o que esta pessoa fez"). */
interface Pivot {
  id: string
  name: string
}

export function AuditLogPage() {
  const session = useSession()
  const viewer: TargetViewer = {
    isRoot: session.profile?.isRoot ?? false,
    clinicId: session.profile?.clinicId ?? null,
  }

  const { filter: targetFilter, setFilter: setTargetFilter } = useStoredFilter(
    'audit-log',
    TARGET_FILTERS,
    'all',
  )
  const [action, setAction] = useState('')
  const [from, setFrom] = useState(() => daysAgoIso(DEFAULT_DAYS))
  const [to, setTo] = useState(() => todayIso())
  const [search, setSearch] = useState('')
  const [actor, setActor] = useState<Pivot | null>(null)
  const [target, setTarget] = useState<Pivot | null>(null)
  const [sort, setSort] = useState<AuditSort>('-occurredAt')
  const [page, setPage] = useState(1)
  const [open, setOpen] = useState<string | null>(null)
  const debouncedSearch = useDebounced(search.trim())

  const query = useAuditLog({
    from,
    to,
    action: action || undefined,
    targetType: targetFilter === 'all' ? undefined : targetFilter,
    targetId: target?.id,
    actorId: actor?.id,
    search: debouncedSearch || undefined,
    sort,
    page,
  })
  const entries = query.data?.data ?? []

  const isDefaultPeriod = from === daysAgoIso(DEFAULT_DAYS) && to === todayIso()
  const hasFilters =
    targetFilter !== 'all' || action !== '' || search !== '' || actor !== null || target !== null
  const isDefaultView = isDefaultPeriod && !hasFilters

  function resetPage() {
    setPage(1)
    setOpen(null)
  }

  function clearFilters() {
    setTargetFilter('all')
    setAction('')
    setSearch('')
    setActor(null)
    setTarget(null)
    setFrom(daysAgoIso(DEFAULT_DAYS))
    setTo(todayIso())
    resetPage()
  }

  function widenPeriod() {
    setFrom(daysAgoIso(WIDER_DAYS))
    setTo(todayIso())
    resetPage()
  }

  return (
    <>
      <PageHeader crumbs={[{ label: 'Administração' }, { label: 'Trilha' }]} />
      <Page wide>
        <PageTitle
          title="Trilha"
          subtitle="O que foi feito nesta clínica, por quem e quando. Só leitura: nada aqui se edita nem se apaga."
        />

        {query.isError ? (
          <LoadError
            className="mt-6"
            title="Não conseguimos carregar a trilha"
            error={query.error}
            onRetry={() => void query.refetch()}
          />
        ) : (
          <>
            <div className="mt-5 flex flex-wrap items-start gap-3">
              <div className="tablet:w-[240px] w-full">
                <Select
                  aria-label="Filtrar pelo que foi alterado"
                  placeholder="Tudo o que foi alterado"
                  value={targetFilter === 'all' ? '' : targetFilter}
                  onChange={(event) => {
                    const value = event.target.value as TargetFilter
                    setTargetFilter(TARGET_FILTERS.includes(value) ? value : 'all')
                    resetPage()
                  }}
                >
                  {TARGET_TYPE_GROUPS.map((group) => (
                    <optgroup key={group.label} label={group.label}>
                      {group.types.map((type) => (
                        <option key={type} value={type}>
                          {targetTypeLabel(type)}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </Select>
              </div>
              <div className="tablet:w-[220px] w-full">
                <Select
                  aria-label="Filtrar por ação"
                  placeholder="Todas as ações"
                  value={action}
                  onChange={(event) => {
                    setAction(event.target.value)
                    resetPage()
                  }}
                >
                  {ACTION_OPTIONS.map((value) => (
                    <option key={value} value={value}>
                      {actionLabel(value)}
                    </option>
                  ))}
                </Select>
              </div>
              <PeriodFilter
                required
                fromLabel="Ações a partir de"
                toLabel="Ações até"
                key={`${from}|${to}`}
                from={from}
                to={to}
                onApply={(nextFrom, nextTo) => {
                  setFrom(nextFrom)
                  setTo(nextTo)
                  resetPage()
                }}
              />
              <SearchInput
                className="tablet:max-w-[300px]"
                value={search}
                onChange={(value) => {
                  setSearch(value)
                  resetPage()
                }}
                placeholder="Buscar no resumo"
                aria-label="Buscar na trilha"
              />
            </div>

            {hasFilters || !isDefaultPeriod ? (
              <div className="font-body text-secondary mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-[12px] leading-[1.6] font-light">
                {actor ? (
                  <span>
                    Só o que {actor.name} fez ·{' '}
                    <Button
                      variant="text"
                      onClick={() => {
                        setActor(null)
                        resetPage()
                      }}
                    >
                      limpar
                    </Button>
                  </span>
                ) : null}
                {target ? (
                  <span>
                    Só o que aconteceu com {target.name} ·{' '}
                    <Button
                      variant="text"
                      onClick={() => {
                        setTarget(null)
                        resetPage()
                      }}
                    >
                      limpar
                    </Button>
                  </span>
                ) : null}
                <Button variant="text" onClick={clearFilters}>
                  Limpar filtros
                </Button>
              </div>
            ) : null}

            <div className="mt-4">
              {query.isPending ? (
                <TableSkeleton rows={6} columns={4} />
              ) : entries.length === 0 ? (
                isDefaultView ? (
                  <EmptyState
                    title={`Nada registrado nos últimos ${DEFAULT_DAYS} dias`}
                    description="Toda ação feita nesta clínica entra aqui: cadastro, bloqueio, troca de papel, envio e assinatura. O que veio antes continua guardado; amplie o período para ver."
                    action={
                      <Button variant="outline" onClick={widenPeriod}>
                        Ver os últimos {WIDER_DAYS} dias
                      </Button>
                    }
                  />
                ) : (
                  <EmptyState
                    title="Nenhuma ação com esses filtros"
                    description="A busca procura no resumo de cada ação; os filtros valem juntos, dentro do período escolhido."
                    action={
                      <Button variant="outline" onClick={clearFilters}>
                        Limpar filtros
                      </Button>
                    }
                  />
                )
              ) : (
                <Table>
                  <TableHeader>
                    <tr>
                      <TableHead>Quem</TableHead>
                      <TableHead>Ação</TableHead>
                      <TableHead>Alvo</TableHead>
                      <TableHead
                        numeric
                        sorted={sort === '-occurredAt' ? 'desc' : 'asc'}
                        onSort={() => {
                          setSort(sort === '-occurredAt' ? 'occurredAt' : '-occurredAt')
                          resetPage()
                        }}
                      >
                        Quando
                      </TableHead>
                      <TableHead aria-label="Ações" />
                    </tr>
                  </TableHeader>
                  <TableBody>
                    {entries.map((entry) => (
                      <AuditRow
                        key={entry.id}
                        entry={entry}
                        viewer={viewer}
                        isOpen={open === entry.id}
                        onToggle={() => setOpen(open === entry.id ? null : entry.id)}
                        onFilterActor={(pivot) => {
                          setActor(pivot)
                          resetPage()
                        }}
                        onFilterTarget={(pivot) => {
                          setTarget(pivot)
                          resetPage()
                        }}
                      />
                    ))}
                  </TableBody>
                </Table>
              )}
              {query.data && entries.length > 0 ? (
                <ListFooter
                  meta={query.data.meta}
                  noun={['registro', 'registros']}
                  note={
                    sort === '-occurredAt'
                      ? 'do mais recente para o mais antigo'
                      : 'do mais antigo para o mais recente'
                  }
                  onPage={(next) => {
                    setPage(next)
                    setOpen(null)
                  }}
                />
              ) : null}
            </div>
          </>
        )}
      </Page>
    </>
  )
}

/**
 * Uma ação da trilha. A linha lê como frase: quem · fez o quê (com o resumo) · com o quê · quando.
 * O clique abre o detalhe logo abaixo: data e hora exatas, resumo inteiro, justificativa e o código
 * da requisição, mais as saídas (abrir o alvo, filtrar por quem agiu ou pelo alvo).
 */
function AuditRow({
  entry,
  viewer,
  isOpen,
  onToggle,
  onFilterActor,
  onFilterTarget,
}: {
  entry: AuditEntry
  viewer: TargetViewer
  isOpen: boolean
  onToggle: () => void
  onFilterActor: (pivot: Pivot) => void
  onFilterTarget: (pivot: Pivot) => void
}) {
  const when = new Date(entry.occurredAt)
  const exact = `${formatDate(when, { year: true })} às ${formatTime(when)}`
  const who = actorName(entry.actor)
  const note = actorNote(entry.actor)
  const what = targetName(entry.target)
  const href = targetRoute(entry.target, viewer)
  const detailId = `audit-detail-${entry.id}`
  const actorId = canFilterByActor(entry.actor) ? entry.actor.id : null
  const targetId = entry.target.id

  return (
    <>
      <TableRow onClick={onToggle}>
        <TableCell primary>
          {who}
          {note ? (
            <span className="font-body text-secondary block text-[12px] leading-[1.5] font-light">
              {note}
            </span>
          ) : null}
        </TableCell>
        <TableCell label="Ação">
          <span className="block">{actionLabel(entry.action)}</span>
          {entry.summary ? (
            <span className="text-secondary desk:max-w-[320px] block truncate text-[12px] leading-[1.5]">
              {entry.summary}
            </span>
          ) : null}
        </TableCell>
        <TableCell label="Alvo">
          <span className="block">{what}</span>
          {entry.target.label ? (
            <span className="text-secondary block text-[12px] leading-[1.5]">
              {targetTypeLabel(entry.target.type)}
            </span>
          ) : null}
        </TableCell>
        <TableCell label="Quando" muted numeric className="whitespace-nowrap">
          <time dateTime={entry.occurredAt} title={exact}>
            {formatRelative(when)}
          </time>
        </TableCell>
        <TableCell>
          <RowActions>
            <button
              type="button"
              aria-expanded={isOpen}
              aria-controls={detailId}
              onClick={onToggle}
            >
              {isOpen ? 'fechar' : 'detalhes'}
            </button>
          </RowActions>
        </TableCell>
      </TableRow>
      {isOpen ? (
        <tr id={detailId} className="border-hairline/60 bg-pampas max-desk:block border-b">
          <td colSpan={5} className="max-desk:block px-4 py-4">
            <dl className="tablet:grid-cols-2 wide:grid-cols-4 grid gap-x-8 gap-y-3">
              <div>
                <dt className="eyebrow">Quando</dt>
                <dd className="font-body text-ink mt-1 text-[13px] leading-[1.5] font-light">
                  {exact}
                </dd>
              </div>
              <div className="tablet:col-span-2 wide:col-span-3">
                <dt className="eyebrow">Resumo</dt>
                <dd className="font-body text-ink mt-1 text-[13px] leading-[1.5] font-light">
                  {entry.summary ?? <span className="text-label">Sem resumo</span>}
                </dd>
              </div>
              {entry.justification ? (
                <div className="tablet:col-span-2 wide:col-span-4">
                  <dt className="eyebrow">Justificativa</dt>
                  <dd className="font-body text-ink mt-1 text-[13px] leading-[1.5] font-light">
                    {entry.justification}
                  </dd>
                </div>
              ) : null}
              <div className="tablet:col-span-2 wide:col-span-4">
                <dt className="eyebrow">Código da requisição</dt>
                <dd className="font-body text-ink mt-1 text-[13px] leading-[1.5] font-light">
                  {entry.requestId ?? <span className="text-label">Sem código</span>}
                </dd>
              </div>
            </dl>
            <div className="font-body text-eyebrow text-label mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 font-light tracking-normal">
              {href ? (
                <Link
                  to={href}
                  className="hover:text-ink min-h-touch-desk max-desk:min-h-touch flex items-center"
                >
                  {openTargetLabel(entry.target)}
                </Link>
              ) : null}
              {actorId ? (
                <button
                  type="button"
                  onClick={() => onFilterActor({ id: actorId, name: who })}
                  className="hover:text-ink min-h-touch-desk max-desk:min-h-touch cursor-pointer"
                >
                  Só o que {who} fez
                </button>
              ) : null}
              {targetId ? (
                <button
                  type="button"
                  onClick={() => onFilterTarget({ id: targetId, name: what })}
                  className="hover:text-ink min-h-touch-desk max-desk:min-h-touch cursor-pointer"
                >
                  Só o que aconteceu com {what}
                </button>
              ) : null}
            </div>
          </td>
        </tr>
      ) : null}
    </>
  )
}
